import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import deprecated from '../../utils/deprecated'

/**
 * Bronson NotificationsGroup component.
 * Groups all related {@link Notification}.
 *
 * @param {Notification|Notification[]} [children] - A collection of {@link Notification} components.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The NotificationsGroup component.
 * @constructor
 */
export function NotificationsGroup({ children, testId, ...otherProps }) {
  return (
    <section
      id="js-notifications-group"
      className="c-notifications-group js-panelgroup js-is-ready js-is-fx is-stacked"
      data-testid={testId}
      {...otherProps}
    >
      {children}
    </section>
  )
}

NotificationsGroup.propTypes = {
  children: PropTypes.node, // Bronson template: 'notifications'. Use {@link Notification} component.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}

/**
 * Bronson Notification component (nested).
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-notification.html
 *
 * @param {Button[]} [buttons=null] - A collection of {@link Button} to render after the text content.
 * @param {string} [buttonText] - Deprecated in favor of {@link buttons}.
 * @param {string|React.ReactNode} [children] - The Notification content.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {string} [closeLabel='Close Notification'] - The label of the close button.
 *                                                     Only applicable when {@link showCloseButton} is set to `true`.
 * @param {boolean} [context] - Render the context variant.
 *                              Should only be used with {@link ContextNotification}.
 * @param {string} [icon] - Deprecated in favor of {@link status}.
 * @param {boolean} [notClosable] - Deprecated. Will be replaced via {@link showCloseButton}.
 * @param {function} [onButtonClick] - Deprecated. Was renamed to {@link onCloseClick}.
 * @param {function} [onCloseClick] - Callback when the optional close button (x) is clicked.
 *                                    Only applicable when {@link showCloseButton} is set to `true`.
 * @param {boolean} [showCloseButton=true] - Show/hide the Notification close button (x).
 * @param {boolean} [shown] - Deprecated. Use {@link visible} instead.
 * @param {null|'info'|'success'|'warning'|'error'} [status] - Optional status that tints the Notification.
 * @param {string} [text] - Optional text content. Takes precedence over {@link children}.
 * @param {string} [title] - Optional title headline to display.
 * @param {boolean} [visible] - Hide/show the Notification.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element|null} - The Notification component.
 * @constructor
 */
export function Notification({
  buttons = null,
  buttonText,
  children,
  className,
  closeLabel = 'Close Notification',
  context,
  icon,
  notClosable,
  onButtonClick,
  onCloseClick,
  showCloseButton = true,
  shown,
  status,
  text,
  title,
  visible,
  testId,
  ...otherProps
}) {
  const divClassName = classNames(
    'c-notification',
    {
      'c-notification--context': context,
      [`c-notification--${status}`]: status,
    },
    className
  ).trim()

  function renderButtons() {
    if (buttons) {
      return <div className="c-notification__buttons">{buttons}</div>
    }
    return null
  }

  function renderCloseButton() {
    if (showCloseButton) {
      if (!notClosable || !buttons) {
        return (
          <button
            className="c-notification__close js-notification-close"
            title={buttonText || closeLabel}
            type="button"
            onClick={onCloseClick || onButtonClick}
          >
            <span className="u-visually-hidden">{buttonText || closeLabel}</span>
          </button>
        )
      }
    }
    return null
  }

  function renderIfStatus() {
    return status ? <i className="c-notification__status-icon" aria-hidden="true" role="img" /> : null
  }

  const iClassName = classNames(
    'c-notification__status-icon',
    'c-icon',
    {
      [`c-icon--[${icon}]`]: icon,
    },
    className
  ).trim()

  function renderIfIcon() {
    return icon ? <i className={iClassName} aria-hidden="true" role="img" /> : null
  }

  function renderIfTitle() {
    return title ? <h3 className="c-notification__title">{title}</h3> : null
  }

  function renderText() {
    /**
     * If {@link text} is set, render it as content for `.c-notification__text`
     * and render {@link children} as sibling elements of `.c-notification__text`.
     * Else render out {@link children} as content for `.c-notification__text` only.
     */
    return (
      <>
        <p className="c-notification__text">{text || children}</p>
        {text ? children : ''}
      </>
    )
  }

  /**
   * Always use the {@link visible} value but fallback on {@link shown} until removed.
   * @deprecated Remove `shown` in the next major.
   */
  return visible ?? shown ? (
    <div className="c-notification__container js-notification-container js-is-ready js-is-fx" data-testid={testId}>
      <div className={divClassName} {...otherProps}>
        <div className="c-notification__outer-wrap">
          <div className="c-notification__inner-wrap">
            {renderIfStatus()}
            {renderIfIcon()}
            <div className="c-notification__content">
              {renderIfTitle()}
              {renderText()}
              {renderButtons()}
            </div>
            {renderCloseButton()}
          </div>
        </div>
      </div>
    </div>
  ) : null
}

Notification.propTypes = {
  buttons: PropTypes.node, // Bronson template: 'buttons'.
  children: PropTypes.node,
  className: PropTypes.string, // Bronson template: 'notification-modifier'.
  closeLabel: PropTypes.string, // Bronson template: 'close.label'.
  context: PropTypes.bool, // Bronson template: 'notification-context'.
  onCloseClick: PropTypes.func, // Added for `[onClick]` attribute.
  showCloseButton: PropTypes.bool, // Bronson template: 'close'.
  status: PropTypes.oneOf([null, 'info', 'success', 'warning', 'error']), // Bronson template: 'notification-status'.
  text: PropTypes.string, // Bronson template: 'notification-text'.
  title: PropTypes.string, // Bronson template: 'notification-title'.
  visible: PropTypes.bool, // Drop-in replacement for `shown`.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
  /**
   *  Deprecated props.
   */
  buttonText: deprecated(
    PropTypes.string,
    'Please use `buttons` and provide the `<Button>` you need. Prop will be removed in Bronson-React v6.0.0.'
  ), // Bronson template: 'notification-button'.
  icon: deprecated(PropTypes.string, 'Please use `status` instead. Prop will be removed in Bronson-React v6.0.0.'), // Bronson template: 'notification-icon'.
  notClosable: deprecated(
    PropTypes.bool,
    'Please use `showCloseButton` instead. Prop will be removed in Bronson-React v6.0.0.'
  ),
  onButtonClick: deprecated(
    PropTypes.func,
    'Please use `onCloseClick` instead. Prop will be removed in Bronson-React v6.0.0.'
  ),
  shown: deprecated(PropTypes.bool, 'Please use `visible` instead. Prop will be removed in Bronson-React v6.0.0.'),
}
